import type {AiChatGeneratedAssetType} from '@AiChat/ai-chat.types';
import type {EntityCardEntityType} from '@Components/ai-chat-window/components/entity-card/entity-card.types';
import type {ChatMessage} from '@Components/ai-chat-window/ai-chat-window.types';
import type {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import type {AiPromptBoxSelection} from '@Components/ai-prompt-box/components/ai-prompt-box-selection-chip/ai-prompt-box-selection-chip.types';

export interface GeneratedImageEvent {
  chatId: string;
  messageId: string | null;
  hashedIds: string[];
  width: number | null;
  height: number | null;
  isFirstGeneration: boolean;
}

export interface GeneratedAssetEvent {
  chatId: string;
  type: AiChatGeneratedAssetType;
  assetHashedFilename: string;
  sourceImageHashedId: string;
  durationInSeconds: number | null;
  hasAudio: boolean | null;
}

export interface MessageSentEvent {
  chatId: string;
}

export interface TurnCompleteEvent {
  chatId: string;
  messages: Record<string, ChatMessage>;
}

export interface GenerationFailedEvent {
  chatId: string;
  message: string;
}

export enum UsedAssetKind {
  IMAGE = 'image',
  ENTITY = 'entity',
}

export interface UsedImageAsset {
  kind: UsedAssetKind.IMAGE;
  chatId: string;
  messageId: string | null;
  imageHashedId: string;
}

export interface UsedEntityAsset {
  kind: UsedAssetKind.ENTITY;
  chatId: string;
  entityType: EntityCardEntityType;
  entity: Record<string, unknown>;
}

export type UseAssetEvent = UsedImageAsset | UsedEntityAsset;

export interface HostContextRequest {
  chatId: string;
  messageSessionUid: string;
}

export interface HostEvent {
  chatId: string;
  type: string;
  data: unknown;
}

export interface TurnStoppedEvent {
  chatId: string;
}

export enum PublishedAssetKind {
  IMAGE = 'image',
  ANIMATION = 'animation',
  DESIGN = 'design',
}

export interface PublishedImageAsset {
  kind: PublishedAssetKind.IMAGE;
  chatId: string;
  option: PublishingOption;
  imageHashedId: string;
}

export interface PublishedAnimationAsset {
  kind: PublishedAssetKind.ANIMATION;
  chatId: string;
  option: PublishingOption;
  animationHashedId: string;
}

/** The user's own saved design, named by its poster and, when the agent pinned one, its page. */
export interface PublishedDesignAsset {
  kind: PublishedAssetKind.DESIGN;
  chatId: string;
  option: PublishingOption;
  posterHashedId: string;
  pageHashedId: string | null;
}

export type PublishingOptionChosenEvent = PublishedImageAsset | PublishedAnimationAsset | PublishedDesignAsset;

export interface AiChatCallbacks {
  onChatCreated?: (chatId: string) => void;
  onMessageSent?: (event: MessageSentEvent) => void;
  onImageGenerated?: (event: GeneratedImageEvent) => void;
  onAssetGenerated?: (event: GeneratedAssetEvent) => void;
  onTurnComplete?: (event: TurnCompleteEvent) => void;
  onGenerationFailed?: (event: GenerationFailedEvent) => void;
  onUseAsset?: (event: UseAssetEvent) => void;
  getHostContext?: (request: HostContextRequest) => Promise<unknown>;
  onHostEvent?: (event: HostEvent) => void;
  onTurnStopped?: (event: TurnStoppedEvent) => void;
  onSelectionRemoved?: (selection: AiPromptBoxSelection) => void;
  onPublishingOptionChosen?: (event: PublishingOptionChosenEvent) => void;
}
